import React, { FC, useCallback, useRef, useState } from 'react';
import { Form, Radio, Select, Typography, Checkbox, Input } from 'antd';

import TextArea from 'antd/es/input/TextArea';
import { v4 as uuidv4 } from 'uuid';
import { useAppDispatch } from '../../../hooks/redux-hooks';
import {
  addFormIdData,
  updateBookingClientData,
  updateClientHealthSummaryData,
} from '../../../redux/slices/booking';
import { removeEmptyStrFields } from '../../../utils/object-check';
import { DATE_WITH_DASH } from '../../../constants/time-formats';
import {
  ContainerWithForm,
  FormContainer,
} from '../../../components/Container';
import FooterNavigator from '../../../components/FooterNavigator';
import { PHN_DOCUMENTS } from '../../../constants/routes';
import ClientDetailsForm from '../../ClientDetails/ClientDetailsForm';
import { FONT } from '../../../styles/color';
import { getPrivateClientDetailsConfig } from '../../../config/privateBooking';

const HncClientDetails: FC = () => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [isAgreedReferral, setIsAgreedReferral] = useState(false);
  const [isAgreedNotification, setIsAgreedNotification] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);
  const formIdRef = useRef(uuidv4());
  const formId = formIdRef.current;
  const clientDetailsConfig = getPrivateClientDetailsConfig();

  // useEffect(() => {
  //   analytics.logPageViewEvent(FIND_A_SESSION, {
  //     site: SITE_NAME,
  //     page_path: pathname,
  //   });
  //
  //   analytics.trackVpcFormStart({
  //     form_id: formId,
  //     page_path: pathname,
  //   });
  // }, [formId, pathname]);

  const submit = useCallback(async () => {
    try {
      const isConsentRequired = clientDetailsConfig.consent;
      if (isConsentRequired && (!isAgreedReferral || !isAgreedNotification)) {
        setConsentError(
          'Please agree to both consent policies before continuing.',
        );

        // analytics.trackVpcFormError({
        //   form_id: formId,
        //   error_type: 'consent_not_agreed',
        //   page_path: pathname,
        // });
        return false;
      }
      setConsentError(null);
      const values = await form.validateFields();
      const {
        nextOfKin,
        addressResidential,
        diffPostalAddress,
        others,
        ...otherValues
      } = values;

      const addressPostal = diffPostalAddress
        ? values.addressPostal
        : addressResidential;

      dispatch(addFormIdData({ formId }));

      dispatch(
        updateBookingClientData({
          ...removeEmptyStrFields(otherValues),
          addressPostal: removeEmptyStrFields(addressPostal),
          addressResidential: removeEmptyStrFields(addressResidential),
          ...(nextOfKin ? { nextOfKin: removeEmptyStrFields(nextOfKin) } : {}),
          dateOfBirth: values.dateOfBirth.format(DATE_WITH_DASH),
        }),
      );

      if (others) {
        dispatch(updateClientHealthSummaryData(removeEmptyStrFields(others)));
      }

      return true;
    } catch (errorInfo) {
      // analytics.trackVpcFormError({
      //   form_id: formId,
      //   error_type: errorInfo,
      //   page_path: pathname,
      // });
      return false;
    }
  }, [isAgreedReferral, isAgreedNotification, form, dispatch, formId, clientDetailsConfig.consent]);

  return (
    <ContainerWithForm>
      <Typography.Title
        level={2}
        style={{ marginBottom: '20px', color: '#5f6369', fontSize: '36px' }}
      >
        New Patient Registration
      </Typography.Title>

      <Typography.Text
        style={{
          display: 'block',
          fontSize: '18px',
          fontWeight: 300,
        }}
      >
        {clientDetailsConfig.subTitle}
      </Typography.Text>

      {consentError && (
        <Typography.Paragraph
          type="danger"
          style={{ marginTop: '16px', marginBottom: '16px' }}
        >
          {consentError}
        </Typography.Paragraph>
      )}

      {clientDetailsConfig.consent && (
        <>
          <Form.Item
            required
            style={{ marginTop: '24px', marginBottom: '24px' }}
            validateStatus={!isAgreedReferral && consentError ? 'error' : ''}
          >
            <Checkbox
              checked={isAgreedReferral}
              onChange={(e) => {
                setIsAgreedReferral(e.target.checked);
                if (e.target.checked) setConsentError(null);
              }}
            >
              Tick this box to confirm that the consumer has consented to a
              referral to the Amplar Health Virtual Psychology Clinic which
              involves the sharing of their personal and health information with
              Amplar Health.
            </Checkbox>
          </Form.Item>

          <Form.Item
            required
            style={{ marginBottom: '24px' }}
            validateStatus={
              !isAgreedNotification && consentError ? 'error' : ''
            }
          >
            <Checkbox
              checked={isAgreedNotification}
              onChange={(e) => {
                setIsAgreedNotification(e.target.checked);
                if (e.target.checked) setConsentError(null);
              }}
            >
              Tick this box to confirm that the consumer consents to receiving
              phone, email and SMS communications from Amplar Health in order to
              enrol them into the program.
            </Checkbox>
          </Form.Item>
        </>
      )}

      <Typography.Title level={3} style={{ color: FONT }}>
        Client Details
      </Typography.Title>
      <ClientDetailsForm
        form={form}
        extraClientFormItems={(
          <Form.Item
            label={`${clientDetailsConfig.clientIdLabel}`}
            required
            name="mmhClientId"
            rules={[
              {
                required: true,
                message: `Please enter ${clientDetailsConfig.clientIdLabel}`,
              },
            ]}
          >
            <Input
              placeholder={`Please enter ${clientDetailsConfig.clientIdLabel}`}
            />
          </Form.Item>
        )}
      />

      {(clientDetailsConfig.showAboriginalQuestion
        || clientDetailsConfig.showReasonForReferral
        || clientDetailsConfig.mentalHealthQuestion) && (
        <>
          <Typography.Title level={3} style={{ color: FONT }}>
            Others
          </Typography.Title>

          <FormContainer form={form}>
            {clientDetailsConfig.showAboriginalQuestion && (
            <Form.Item
              label="Aboriginal and/or Torres Strait Islander"
              required
              name={['others', 'aboriginalOrTorresStraitIslander']}
              rules={[
                {
                  required: true,
                  message:
                    'Please select whether Aboriginal and/or Torres Strait Islander',
                },
              ]}
            >
              <Select style={{ width: '100%' }}>
                <Select.Option value="aboriginal">Aboriginal</Select.Option>
                <Select.Option value="not aboriginal and/or torres strait islander">
                  Not Aboriginal and/or Torres Strait Islander
                </Select.Option>
                <Select.Option value="aboriginal and torres strait islander">
                  Aboriginal and Torres Strait Islander
                </Select.Option>
                <Select.Option value="torres strait islander">
                  Torres Strait Islander
                </Select.Option>
                <Select.Option value="not stated / inadequately described">
                  Not stated / Inadequately described
                </Select.Option>
              </Select>
            </Form.Item>
            )}

            {clientDetailsConfig.showReasonForReferral && (
              <Form.Item
                label="Reason/s for Referral"
                required
                name={['others', 'reasonForReferral']}
                rules={[
                  { required: true, message: 'Please enter reasons for referrals' },
                ]}
              >
                <TextArea
                  rows={3}
                  placeholder="If more than one reason is applicable, please provide all relevant 'reasons'"
                />
              </Form.Item>
            )}

            {clientDetailsConfig.mentalHealthQuestion && (
              <Form.Item
                label="Does the client have a mental health plan?"
                required
                name={['others', 'hasMentalHealthPlan']}
                rules={[
                  {
                    required: true,
                    message:
                      'Please enter whether the the client have a mental health plan.',
                  },
                ]}
              >
                <Radio.Group
                  options={[
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' },
                  ]}
                />
              </Form.Item>
            )}
          </FormContainer>
        </>
      )}

      <FooterNavigator
        error=""
        submit={submit}
        next={PHN_DOCUMENTS}
        hasBack={false}
        containerStyle={{ marginTop: '40px' }}
      />
    </ContainerWithForm>
  );
};

export default HncClientDetails;
